import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import RestaurantCard from "../components/common/RestaurantCard";
import { Spinner, EmptyState } from "../components/common/UI";

const CITIES = ["All", "Burewala", "Vehari"];
const CUISINES = ["All", "Biryani", "BBQ", "Karahi", "Desi", "Fast Food", "Sweets", "Drinks"];
const SORT_OPTIONS = [
  { value: "rating", label: "Top Rated" },
  { value: "newest", label: "Newest" },
  { value: "name", label: "A-Z" },
];

export default function RestaurantListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [city, setCity] = useState("All");
  const [cuisine, setCuisine] = useState(searchParams.get("cuisine") || "All");
  const [sort, setSort] = useState("rating");

  const fetchRestaurants = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("restaurants")
      .select("*")
      .eq("is_active", true);

    if (search) {
      query = query.ilike("name", `%${search}%`);
    }
    if (city !== "All") {
      query = query.eq("city", city);
    }
    if (cuisine !== "All") {
      query = query.contains("cuisine_type", [cuisine]);
    }
    if (sort === "rating") {
      query = query.order("rating", { ascending: false });
    } else if (sort === "newest") {
      query = query.order("created_at", { ascending: false });
    } else {
      query = query.order("name");
    }

    const { data } = await query;
    setRestaurants(data || []);
    setLoading(false);
  }, [search, city, cuisine, sort]);

  useEffect(() => {
    const timer = setTimeout(fetchRestaurants, 300);
    return () => clearTimeout(timer);
  }, [fetchRestaurants]);

  const clearFilters = () => {
    setSearch("");
    setCity("All");
    setCuisine("All");
    setSort("rating");
    setSearchParams({});
  };

  const hasFilters = search || city !== "All" || cuisine !== "All";

  return (
    <>
      <Helmet>
        <title>All Restaurants — Khana Spot</title>
        <meta name="description" content="Browse restaurants in Burewala and Vehari. Find your favorite food." />
      </Helmet>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 page-enter">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold text-gray-900">All Restaurants</h1>
          <p className="text-gray-500 mt-1">
            {loading ? "Loading..." : `${restaurants.length} restaurant${restaurants.length !== 1 ? "s" : ""} found`}
          </p>
        </div>

        {/* Search + Filter bar */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search restaurants..."
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 border rounded-xl font-medium transition-colors ${
              showFilters ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-700 hover:border-orange-300"
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-3 text-red-500 hover:bg-red-50 border border-red-200 rounded-xl transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* City */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">City</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CITIES.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCity(c)}
                      className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                        city === c
                          ? "bg-orange-500 text-white border-orange-500"
                          : "border-gray-200 text-gray-600 hover:border-orange-300"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cuisine */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cuisine</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CUISINES.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCuisine(c)}
                      className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                        cuisine === c
                          ? "bg-orange-500 text-white border-orange-500"
                          : "border-gray-200 text-gray-600 hover:border-orange-300"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sort By</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SORT_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => setSort(o.value)}
                      className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                        sort === o.value
                          ? "bg-orange-500 text-white border-orange-500"
                          : "border-gray-200 text-gray-600 hover:border-orange-300"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : restaurants.length === 0 ? (
          <EmptyState
            icon="🔍"
            title="No restaurants found"
            description="Try adjusting your search or filters to find what you're looking for."
            action={<button onClick={clearFilters} className="btn-primary">Clear Filters</button>}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {restaurants.map((r) => (
              <RestaurantCard key={r.id} restaurant={r} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
